import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../services/firebase';
import { AuthContextType, User, SignupData } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Mock verification code storage
const getVerificationCodes = () => {
  const stored = localStorage.getItem('verificationCodes');
  return stored ? JSON.parse(stored) : {};
};

const setVerificationCode = (identifier: string, code: string, expiresAt: number) => {
  const codes = getVerificationCodes();
  codes[identifier] = { code, expiresAt };
  localStorage.setItem('verificationCodes', JSON.stringify(codes));
};

const getVerificationCode = (identifier: string) => {
  const codes = getVerificationCodes();
  return codes[identifier];
};

const clearVerificationCode = (identifier: string) => {
  const codes = getVerificationCodes();
  delete codes[identifier];
  localStorage.setItem('verificationCodes', JSON.stringify(codes));
};

// Generate random 6-digit code
const generateCode = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (isFirebaseConfigured && auth) {
      // Real Firebase auth
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          // TODO: Fetch subscription status and verification status from backend
          const subscriptionActive = localStorage.getItem('mockSubscriptionActive') === 'true';
          const storedUserData = localStorage.getItem(`user_${firebaseUser.uid}`);
          const parsedData = storedUserData ? JSON.parse(storedUserData) : {};
          
          const userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || undefined,
            firstName: parsedData.firstName,
            lastName: parsedData.lastName,
            phoneNumber: parsedData.phoneNumber,
            passwordHint: parsedData.passwordHint,
            verified: parsedData.verified || false,
            subscriptionActive,
            createdAt: parsedData.createdAt ? new Date(parsedData.createdAt) : new Date(),
          };
          
          setUser(userData);
          
          // Update localStorage auth tokens for game subdomain
          localStorage.setItem('authToken', firebaseUser.uid);
          localStorage.setItem('userEmail', firebaseUser.email || '');
          localStorage.setItem('subscriptionActive', subscriptionActive ? 'true' : 'false');
          // Only set loginTimestamp if not already set (to preserve original login time)
          if (!localStorage.getItem('loginTimestamp')) {
            localStorage.setItem('loginTimestamp', Date.now().toString());
          }
        } else {
          setUser(null);
        }
        setLoading(false);
      });
    } else {
      // Mock auth for development - non-blocking
      const timeoutId = setTimeout(() => {
        const mockUser = localStorage.getItem('mockUser');
        if (mockUser) {
          try {
            const parsed = JSON.parse(mockUser);
            // Only set user if verified
            if (parsed.verified) {
              setUser(parsed);
              
              // Update localStorage auth tokens for game subdomain
              localStorage.setItem('authToken', parsed.uid);
              localStorage.setItem('userEmail', parsed.email);
              localStorage.setItem('subscriptionActive', parsed.subscriptionActive ? 'true' : 'false');
              // Only set loginTimestamp if not already set (to preserve original login time)
              if (!localStorage.getItem('loginTimestamp')) {
                localStorage.setItem('loginTimestamp', Date.now().toString());
              }
            }
          } catch (e) {
            console.warn('Failed to parse mock user:', e);
          }
        }
        setLoading(false);
      }, 0);

      return () => clearTimeout(timeoutId);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const signup = async (userData: SignupData) => {
    // TODO: In production, integrate with Firebase Phone Authentication
    // TODO: In production, integrate with SendGrid for email verification
    // TODO: In production, integrate with Twilio for SMS verification
    
    if (isFirebaseConfigured && auth) {
      // Real Firebase signup - create account but mark as unverified
      const result = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      
      // Store user data temporarily
      const tempUser: User = {
        uid: result.user.uid,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber,
        passwordHint: userData.passwordHint,
        verified: false,
        subscriptionActive: false,
        createdAt: new Date(),
      };
      
      localStorage.setItem(`user_${result.user.uid}`, JSON.stringify(tempUser));
      
      // Send verification code
      await sendVerificationCode('email', userData.email);
      
      console.log('User created (unverified):', result.user.uid);
    } else {
      // Mock signup - store temporarily, don't create account yet
      const tempUser: User = {
        uid: 'temp-' + Date.now(),
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber,
        passwordHint: userData.passwordHint,
        verified: false,
        subscriptionActive: false,
        createdAt: new Date(),
      };
      
      localStorage.setItem('tempUser', JSON.stringify(tempUser));
      
      // Send verification code
      await sendVerificationCode('email', userData.email);
    }
  };

  const login = async (email: string, password: string) => {
    if (isFirebaseConfigured && auth) {
      await signInWithEmailAndPassword(auth, email, password);
      
      // Check if user is verified
      const userData = localStorage.getItem(`user_${auth.currentUser?.uid}`);
      if (userData) {
        const parsed = JSON.parse(userData);
        if (!parsed.verified) {
          await signOut(auth);
          throw new Error('Account not verified. Please verify your email/phone first.');
        }
      }
      
      // After successful login, store auth data for game subdomain
      if (auth.currentUser) {
        const subscriptionActive = localStorage.getItem('mockSubscriptionActive') === 'true';
        localStorage.setItem('authToken', auth.currentUser.uid);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('subscriptionActive', subscriptionActive ? 'true' : 'false');
        localStorage.setItem('loginTimestamp', Date.now().toString());
        
        console.log('✅ Auth data stored (Firebase):', {
          token: auth.currentUser.uid,
          email: email,
          subscription: subscriptionActive
        });
      }
    } else {
      // Mock login - check users array first, then mockUser
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      const user = users.find((u: any) => u.email === email);
      
      if (user) {
        // Verify password (in real app, this would be hashed)
        // Note: Password might be stored in pendingUser during signup
        // For now, check if user is verified
        if (!user.verified) {
          throw new Error('Account not verified. Please verify your email/phone first.');
        }
        
        // ✅ STORE AUTH DATA IN LOCALSTORAGE
        localStorage.setItem('authToken', user.uid);
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('subscriptionActive', user.subscriptionActive ? 'true' : 'false');
        localStorage.setItem('loginTimestamp', Date.now().toString());
        
        console.log('✅ Auth data stored:', {
          token: user.uid,
          email: user.email,
          subscription: user.subscriptionActive
        });
        
        // Set user in context
        setUser(user);
        
        return;
      }
      
      // Fallback to mockUser (legacy)
      const mockUser = localStorage.getItem('mockUser');
      if (mockUser) {
        const parsedUser = JSON.parse(mockUser);
        if (parsedUser.email === email && parsedUser.verified) {
          setUser(parsedUser);
          
          // Store auth data for game subdomain
          const subscriptionActive = parsedUser.subscriptionActive || false;
          localStorage.setItem('authToken', parsedUser.uid);
          localStorage.setItem('userEmail', email);
          localStorage.setItem('subscriptionActive', subscriptionActive ? 'true' : 'false');
          localStorage.setItem('loginTimestamp', Date.now().toString());
          
          console.log('✅ Auth data stored (mockUser):', {
            token: parsedUser.uid,
            email: email,
            subscription: subscriptionActive
          });
          
          return;
        } else if (parsedUser.email === email && !parsedUser.verified) {
          throw new Error('Account not verified. Please verify your email/phone first.');
        }
      }
      throw new Error('Invalid credentials (mock mode)');
    }
  };

  const logout = async () => {
    // Clear auth data from localStorage (for game subdomain)
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('subscriptionActive');
    localStorage.removeItem('loginTimestamp');
    
    if (isFirebaseConfigured && auth) {
      await signOut(auth);
    } else {
      localStorage.removeItem('mockUser');
      setUser(null);
    }
  };

  const resetPassword = async (email: string) => {
    // Legacy function - redirects to resetPasswordWithCode flow
    if (isFirebaseConfigured && auth) {
      await sendPasswordResetEmail(auth, email);
    } else {
      console.log('Password reset email sent (mock mode)');
    }
  };

  const sendVerificationCode = async (method: 'email' | 'phone', identifier: string): Promise<void> => {
    // TODO: In production, use Firebase Phone Authentication
    // TODO: In production, use SendGrid for email
    // TODO: In production, use Twilio for SMS
    
    const code = generateCode();
    const expiresAt = Date.now() + 5 * 60 * 1000; // 5 minutes
    
    setVerificationCode(identifier, code, expiresAt);
    
    if (method === 'email') {
      console.log(`[MOCK] Verification code sent to ${identifier}: ${code}`);
      // TODO: SendGrid integration
    } else {
      console.log(`[MOCK] SMS verification code sent to ${identifier}: ${code}`);
      // TODO: Twilio integration
    }
  };

  const verifyCode = async (code: string): Promise<boolean> => {
    // Get the temp user to find their identifier
    const tempUser = localStorage.getItem('tempUser');
    if (!tempUser) {
      // Try to get from Firebase user
      if (auth?.currentUser) {
        const userData = localStorage.getItem(`user_${auth.currentUser.uid}`);
        if (userData) {
          const parsed = JSON.parse(userData);
          const identifier = parsed.email || parsed.phoneNumber;
          const stored = getVerificationCode(identifier);
          
          if (stored && stored.code === code) {
            if (Date.now() > stored.expiresAt) {
              clearVerificationCode(identifier);
              throw new Error('Code expired. Please request a new one.');
            }
            
            // Mark as verified
            parsed.verified = true;
            localStorage.setItem(`user_${auth.currentUser.uid}`, JSON.stringify(parsed));
            clearVerificationCode(identifier);
            return true;
          }
        }
      }
      return false;
    }
    
    const parsed = JSON.parse(tempUser);
    const identifier = parsed.email || parsed.phoneNumber;
    const stored = getVerificationCode(identifier);
    
    if (stored && stored.code === code) {
      if (Date.now() > stored.expiresAt) {
        clearVerificationCode(identifier);
        throw new Error('Code expired. Please request a new one.');
      }
      
      // Create verified user
      parsed.verified = true;
      parsed.uid = 'mock-' + Date.now();
      localStorage.setItem('mockUser', JSON.stringify(parsed));
      localStorage.removeItem('tempUser');
      clearVerificationCode(identifier);
      setUser(parsed);
      return true;
    }
    
    return false;
  };

  const resetPasswordWithCode = async (identifier: string, code: string, newPassword: string): Promise<void> => {
    // TODO: In production, verify code with Firebase/Twilio
    // TODO: In production, update password in Firebase
    
    const stored = getVerificationCode(identifier);
    
    if (!stored || stored.code !== code) {
      throw new Error('Invalid verification code. Please try again.');
    }
    
    if (Date.now() > stored.expiresAt) {
      clearVerificationCode(identifier);
      throw new Error('Code expired. Please request a new one.');
    }
    
    // Find user by email or phone
    const mockUser = localStorage.getItem('mockUser');
    if (mockUser) {
      const parsed = JSON.parse(mockUser);
      if (parsed.email === identifier || parsed.phoneNumber === identifier) {
        // Update password (in production, this would update Firebase)
        parsed.password = newPassword; // Note: In production, hash this
        localStorage.setItem('mockUser', JSON.stringify(parsed));
        clearVerificationCode(identifier);
        return;
      }
    }
    
    // TODO: Handle Firebase user password reset
    throw new Error('User not found');
  };

  const value: AuthContextType = {
    user,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    verifyCode,
    sendVerificationCode,
    resetPasswordWithCode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
